import { createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { empresasService } from "app/services";
import { toast } from "react-hot-toast";
import { ErrorMessage, Spinner } from "app/components";

const CorreosElectronicosContext = createContext();

export function CorreosElectronicosProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const correosElectronicosQuery = useQuery({
    queryKey: ["empresa:get:correos-electronicos"],
    queryFn: () =>
      empresasService.getCorreosElectronicos({ empresaId: params.empresa_id }),
  });

  const correoElectronicoAddMutation = useMutation({
    queryKey: ["empresa:add:correo-electronico", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addCorreoElectronico(data), {
        loading: "Agregando la dirección de correo electrónico...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await correosElectronicosQuery.refetch();
    },
  });

  const correoElectronicoUpdateMutation = useMutation({
    queryKey: ["empresa:update:correo-electronico"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateCorreoElectronico(data), {
        loading: "Actualizando la dirección de correo electrónico...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await correosElectronicosQuery.refetch();
    },
  });

  const correoElectronicoDeleteMutation = useMutation({
    queryKey: ["empresa:delete:correo-electronico"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteCorreoElectronico(data), {
        loading: "Eliminando la dirección correo electrónico...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await correosElectronicosQuery.refetch();
    },
  });

  const correoElectronicoVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:correo-electronico"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyCorreoElectronico(data), {
        loading: "Verificando la dirección de correo electrónico...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await correosElectronicosQuery.refetch();
    },
  });

  async function addCorreoElectronico({ correoElectronico }) {
    return await correoElectronicoAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      correoElectronico,
    });
  }

  async function updateCorreoElectronico({
    correoElectronicoId,
    correoElectronico,
  }) {
    const { correo_electronico_verificado, ...rest } = correoElectronico;

    return await correoElectronicoUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      correoElectronicoId,
      correoElectronico: rest,
    });
  }

  async function deleteCorreoElectronico({ correoElectronicoId }) {
    const deleted = window.confirm(
      "¿Desea eliminar esta dirección de correo electrónico?"
    );

    if (!!deleted) {
      await correoElectronicoDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        correoElectronicoId: correoElectronicoId,
      });

      return true;
    }

    return false;
  }

  async function verifyCorreoElectronico({ correoElectronicoId }) {
    const confirmed = window.confirm(
      "¿Desea verificar esta dirección de correo electrónico?"
    );

    if (confirmed) {
      return await correoElectronicoVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        correoElectronicoId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!correosElectronicosQuery.isLoading) {
    return (
      <Spinner label="Cargando las direcciones de correo electrónico..." />
    );
  } else if (!!correosElectronicosQuery.isError) {
    return (
      <ErrorMessage
        message={correosElectronicosQuery.error.response.statusText}
      />
    );
  }

  return (
    <CorreosElectronicosContext.Provider
      value={{
        correosElectronicos: correosElectronicosQuery.data,
        isLoading: correosElectronicosQuery.isLoading,
        isFetching: correosElectronicosQuery.isFetching,
        isRefetching: correosElectronicosQuery.isRefetching,
        addCorreoElectronico,
        isAdding: correoElectronicoAddMutation.isLoading,
        updateCorreoElectronico,
        isUpdating: correoElectronicoUpdateMutation.isLoading,
        deleteCorreoElectronico,
        isDeleting: correoElectronicoDeleteMutation.isLoading,
        verifyCorreoElectronico,
        isVerifing: correoElectronicoVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </CorreosElectronicosContext.Provider>
  );
}

export function useCorreosElectronicosContext() {
  const correosElectronicosContext = useContext(CorreosElectronicosContext);

  if (!correosElectronicosContext) {
    throw new Error(
      "useCorreosElectronicosContext should be used inside the CorreosElectronicosProvider."
    );
  }

  return correosElectronicosContext;
}
